import { useCallback, useEffect, useRef } from 'react';
import type { Step } from '../models/Step';
import { useFormContext } from '../context/FormContext';
import { formBasePath } from '../services/formUrls';

/**
 * Mirrors the active step into the address bar, e.g. '.../quarterly-report-form/step-2/'.
 *
 * Opt in per form by setting `stepUrlPrefix` in the app settings. The prefix has to
 * match the server route that serves the form for those URLs (see the $url_handlers
 * entry on the form's controller), otherwise a refresh 404s.
 *
 * Steps are numbered by position rather than id so the URL matches the step labels
 * the user sees ("Step 2" -> /step-2/).
 *
 * Note this reflects position only — it does not restore it. The form keeps no saved
 * state, so loading /step-4/ directly starts a fresh report at step 1 and rewrites the
 * URL to match rather than showing an empty step 4.
 */
export function useStepUrl(): void {
  const { settings, formService, forceUpdate } = useFormContext();

  const prefix = settings.stepUrlPrefix;
  const activeStep = formService.getActiveStep();
  const activeStepId = activeStep ? activeStep.id : '';

  // The first sync replaces the entry URL ('/start/') instead of pushing, so Back
  // from step 1 returns to the page the user came from rather than to /start/.
  const hasSyncedOnce = useRef<boolean>(false);

  const pathForStep = useCallback<(step: Step) => string>(
    (step) => `${formBasePath(settings.apiBaseUrl)}${prefix}${formService.getStepIndex(step) + 1}/`,
    [settings.apiBaseUrl, prefix, formService]
  );

  const stepForPath = useCallback<(path: string) => Step | null>(
    (path) => {
      const match = new RegExp(`${prefix}(\\d+)/?$`).exec(path);

      if (!match) {
        return null;
      }

      return formService.getFormData().steps[Number(match[1]) - 1] ?? null;
    },
    [prefix, formService]
  );

  useEffect(() => {
    if (!prefix || !activeStep) {
      return;
    }

    const path = pathForStep(activeStep);

    // Already on the right URL — either nothing moved, or the browser moved us here
    // via back/forward, in which case pushing again would trap the user in the form.
    if (window.location.pathname === path) {
      hasSyncedOnce.current = true;
      return;
    }

    // Keep the current query string (e.g. ?demoPrefill=1) so refresh still works.
    const url = `${path}${window.location.search}${window.location.hash}`;

    if (hasSyncedOnce.current) {
      window.history.pushState({ stepId: activeStep.id }, '', url);
    } else {
      window.history.replaceState({ stepId: activeStep.id }, '', url);
    }

    hasSyncedOnce.current = true;
  }, [prefix, activeStep, activeStepId, pathForStep]);

  useEffect(() => {
    if (!prefix) {
      return;
    }

    const onPopState = (): void => {
      const step = stepForPath(window.location.pathname);

      if (step && step !== formService.getActiveStep()) {
        formService.setActiveStep(step);
        forceUpdate();
      }
    };

    window.addEventListener('popstate', onPopState);

    return () => window.removeEventListener('popstate', onPopState);
  }, [prefix, stepForPath, formService, forceUpdate]);
}
